/**
 * @fileoverview ClubOS Authentication & RBAC Context
 * Authenticates via the ClubOS JWT API. On successful login the access token
 * is stored in localStorage under "clubos_token" (read by api/client.js).
 */

import { createContext, useContext, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { ROLE_PERMISSIONS } from "constants/roles";
import apiClient from "api/client";
import { settingsService } from "api";

// Kept for components (e.g. Sidenav) that still reference the demo user list.
export const DEMO_USERS = [
  { id: 1, name: "Rajesh Kumar",   email: "rajesh@fcmetro.in",  role: "Chairman",             avatar: "RK", department: "Management" },
  { id: 2, name: "Priya Sharma",   email: "priya@fcmetro.in",   role: "CEO",                  avatar: "PS", department: "Management" },
  { id: 3, name: "Amit Verma",     email: "amit@fcmetro.in",    role: "CFO",                  avatar: "AV", department: "Finance"    },
  { id: 4, name: "Carlos Mendoza", email: "carlos@fcmetro.in",  role: "Head Coach",           avatar: "CM", department: "Coaching"   },
  { id: 5, name: "Sneha Pillai",   email: "sneha@fcmetro.in",   role: "Finance Executive",    avatar: "SP", department: "Finance"    },
  { id: 6, name: "David Walsh",    email: "david@fcmetro.in",   role: "Chief Scout",          avatar: "DW", department: "Scouting"   },
  { id: 7, name: "Ravi Nair",      email: "ravi@fcmetro.in",    role: "System Administrator", avatar: "RN", department: "IT"         },
];

const TOKEN_KEY   = "clubos_token";
const REFRESH_KEY = "clubos_refresh_token";
const SECTIONS_KEY = "clubos_enabled_sections";

async function syncSettings() {
  try {
    const data = await settingsService.getModules();
    if (Array.isArray(data)) {
      const map = {};
      data.forEach((m) => { map[m.moduleId] = m.isEnabled; });
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(map));
    }
  } catch { /* API unavailable — keep whatever is already in localStorage */ }
}

// Maps API role enum names → display role names used by ROLE_PERMISSIONS
const API_ROLE_TO_DISPLAY = {
  Admin:                 "System Administrator",
  ClubCeo:               "CEO",
  DirectorOfFootball:    "Director of Football",
  ChiefFinancialOfficer: "CFO",
  HeadCoach:             "Head Coach",
  AssistantCoach:        "Assistant Coach",
  Coach:                 "Coach",
  ChiefScout:            "Chief Scout",
  Scout:                 "Scout",
  TeamDoctor:            "Team Doctor",
  Physiotherapist:       "Physiotherapist",
  ClubSecretary:         "Club Secretary",
};

function apiUserToContextUser(apiUser) {
  const initials = (apiUser.fullName || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return {
    id:         apiUser.id,
    name:       apiUser.fullName,
    email:      apiUser.email,
    role:       API_ROLE_TO_DISPLAY[apiUser.role] ?? apiUser.role,
    avatar:     initials,
    department: apiUser.department ?? "—",
    tenantId:   apiUser.tenantId,
  };
}

const AuthContext = createContext(null);
AuthContext.displayName = "ClubOSAuth";

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuth]  = useState(false);
  const [loading, setLoading]         = useState(true);

  // On mount: if a token is already stored, verify it is still valid.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    apiClient.get("/auth/me")
      .then(async (user) => {
        setCurrentUser(apiUserToContextUser(user));
        setIsAuth(true);
        await syncSettings();
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
      })
      .finally(() => setLoading(false));
  }, []);

  /** Sign in with email + password. Throws on failure so the caller can show an error. */
  const login = useCallback(async (email, password) => {
    const tokens = await apiClient.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY,   tokens.accessToken);
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);

    const apiUser = await apiClient.get("/auth/me");
    const user = apiUserToContextUser(apiUser);
    setCurrentUser(user);
    setIsAuth(true);
    await syncSettings();
    return user;
  }, []);

  /** Sign out — revokes the refresh token server-side, then clears local state. */
  const logout = useCallback(async () => {
    try { await apiClient.post("/auth/logout"); } catch { /* ignore network errors */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setCurrentUser(null);
    setIsAuth(false);
  }, []);

  const hasPermission = useCallback(
    (permission) => {
      if (!currentUser) return false;
      return (ROLE_PERMISSIONS[currentUser.role] ?? []).includes(permission);
    },
    [currentUser]
  );

  const hasAnyPermission = useCallback(
    (permissionList) => permissionList.some((p) => hasPermission(p)),
    [hasPermission]
  );

  return (
    <AuthContext.Provider value={{ currentUser, isAuthenticated, loading, login, logout, hasPermission, hasAnyPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = { children: PropTypes.node.isRequired };

/** Hook to consume auth context */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
